import { SparkleIcon } from "assets/icons/Sparkle";
import { orderStatusMapping, orderTypeConstants } from "constants/order";
import {
  calculateDiscountPercent,
  centsToDollars,
  parsePrice,
} from "helpers/price";
import { roundToX } from "helpers/round";
import usePrice from "hooks/usePrice";
import { isEmpty, sumBy } from "lodash";
import {
  StatusBadge,
  STATUS_BADGE_ICONS,
  STATUS_BADGE_TONES,
} from "pages/Orders/components/OrdersTable/components/StatusBadge";
import { useEffect, useMemo } from "react";
import styled from "styled-components";
import {
  IGFEServiceDetails,
  IOrder,
  IPatientResponsibilityServiceDetails,
} from "types/Order/Order";

interface IBreakdownRow {
  label: string;
  value?: number | null;
  prefix?: string;
}

type IPrices = ReturnType<
  ReturnType<typeof usePrice>["getClearPriceByOrderType"]
>;
type IInsuranceAmounts = ReturnType<typeof usePrice>["insuranceAmounts"];

// The difference of two cents amounts, or null when either is missing — the
// way the Redo card derives "You Save" and the prompt-pay discount.
function difference(a?: number | null, b?: number | null) {
  return a != null && b != null ? a - b : null;
}

// The breakdown rows for the order's type: the same labels, order and
// conditions as the Redo (EstimateResults) card, plus every line of the old
// Service Details totals (under their old labels), from the saved order's
// figures (all in cents).
function getBreakdownRows(
  order: IOrder,
  prices: IPrices,
  insuranceAmounts: IInsuranceAmounts,
  clearPatientFee: number
): IBreakdownRow[] {
  if (order.orderType === orderTypeConstants.GFE) {
    const isSelfPayRate =
      (order.serviceDetails as IGFEServiceDetails)?.payerName ===
      "Self-Pay Rate";
    // Same discount badge as ServiceDetails: the discount on the procedure
    // portion alone, with the clear patient fee taken out of both sides.
    const discountPercent = roundToX(
      calculateDiscountPercent(
        prices.total - clearPatientFee,
        prices.clearPrice - clearPatientFee
      ),
      2
    );
    return [
      ...(isSelfPayRate
        ? []
        : [
            {
              label: "Contracted Rate",
              value: insuranceAmounts.totalAllowedInsuranceAmount,
            },
          ]),
      {
        label: "Insurance Pays",
        value: insuranceAmounts.totalInsurancePayInCents,
      },
      {
        label: "Applied to deductible",
        value: insuranceAmounts.totalDeductibleInCents,
      },
      { label: "Coinsurance", value: insuranceAmounts.totalCoinsuranceInCents },
      { label: "Copay", value: insuranceAmounts.totalFlatCopayInCents },
      {
        label:
          clearPatientFee > 0
            ? "Subtotal Patient Estimate"
            : "Total Patient Estimate",
        value:
          clearPatientFee > 0 ? prices.total - clearPatientFee : prices.total,
      },
      ...(clearPatientFee > 0
        ? [
            {
              label: `Clear Patient Fee (${order.clearPatientFeeValueInPercentage}%)`,
              value: clearPatientFee,
              prefix: "+",
            },
            { label: "Total estimated patient cost", value: prices.total },
          ]
        : []),
      ...(discountPercent > 0
        ? [
            {
              label: `Discount (${discountPercent}%)`,
              value: difference(prices.total, prices.clearPrice),
              prefix: "−",
            },
          ]
        : []),
    ];
  }
  if (order.orderType === orderTypeConstants.PATIENT_RESPONSIBILITY) {
    const totalInsuranceAllowable = (
      order.serviceDetails as IPatientResponsibilityServiceDetails
    )?.totalInsuranceAllowableInCents;
    const providerFinalPayment =
      order.paymentFullBreakdown?.patientRespFacilityAmountInCents;
    const discountPercent = roundToX(
      calculateDiscountPercent(prices.total, prices.clearPrice),
      2
    );
    return [
      ...(totalInsuranceAllowable != null
        ? [
            {
              label: "Total Insurance Allowable",
              value: totalInsuranceAllowable,
            },
          ]
        : []),
      { label: "Total Patient Estimate", value: prices.total },
      ...(discountPercent > 0
        ? [
            {
              label: `Discount (${discountPercent}%)`,
              value: difference(prices.total, prices.clearPrice),
              prefix: "−",
            },
          ]
        : []),
      ...(providerFinalPayment
        ? [{ label: "Provider final payment", value: providerFinalPayment }]
        : []),
    ];
  }
  return [
    { label: "Estimated Payment Due", value: prices.facilityFee },
    {
      label: "Subtotal Cost",
      value: sumBy(order.serviceDetails?.procedures as any, "amountInCents"),
    },
    { label: "Bundled Price", value: prices.clearPrice },
    {
      label: "You Save",
      value: difference(prices.facilityFee, prices.clearPrice),
    },
  ];
}

// Same size as the Redo card: full width when stacked, a fixed 360px sticky
// column from 1200px up.
const StyledSummary = styled.aside`
  width: 100%;

  @media (min-width: 1200px) {
    position: sticky;
    top: calc(var(--navbar-height) + var(--content-gap-top));
    width: 360px;
    flex-shrink: 0;
  }
`;

// The right-hand "Estimate Summary" card of the redesigned order view: a
// header with the order status, the amount owed with a collected progress
// bar, and the per-order-type breakdown ending in the patient responsibility.
export function EstimateSummary({ order }: { order: IOrder }) {
  const { getClearPriceByOrderType, insuranceAmounts, getInsuranceAmounts } =
    usePrice();
  const prices = getClearPriceByOrderType({ order });
  const { clearPrice } = prices;
  // Collected = the order's paid payments, summed the way the receipt sums "Total Paid".
  const collectedInCents = useMemo(
    () =>
      (order.payments || [])
        .filter((p) => p.status === "Paid")
        .reduce((sum, p) => sum + (p.amountInCents || 0), 0),
    [order.payments]
  );
  const collectedPercent =
    clearPrice > 0
      ? Math.min(100, Math.round((collectedInCents / clearPrice) * 100))
      : 0;

  useEffect(() => {
    if (!isEmpty(order.gfeQuote)) {
      getInsuranceAmounts(order.gfeQuote.pricingBreakdown);
    }
  }, [order.gfeQuote]);

  // Same clear patient fee as ServiceDetails: the per-row fees, GFE only.
  const clearPatientFee =
    order.orderType === orderTypeConstants.GFE &&
    order.clearPatientFeeValueInPercentage &&
    order.clearPatientFeeValueInPercentage > 0
      ? (order.paymentFullBreakdown?.bundledProviderAmounts ?? []).reduce(
          (sum, row) => sum + (row.clearPatientFeeAmountInCents ?? 0),
          0
        )
      : 0;

  const rows = getBreakdownRows(
    order,
    prices,
    insuranceAmounts,
    clearPatientFee
  ).filter((row) => row.value !== null && row.value !== undefined);

  return (
    <StyledSummary
      className="bg-white border-1 border-round-2xl p-3"
      style={{
        borderColor: "var(--brand-gray-200)",
        boxShadow:
          "0 1px 2px color-mix(in srgb, var(--brand-black) 4%, transparent), 0 6px 16px color-mix(in srgb, var(--brand-black) 8%, transparent)",
      }}
      data-testid="estimate-summary"
    >
      <div className="flex align-items-center gap-3">
        <div
          className="flex align-items-center justify-content-center border-round-2xl flex-shrink-0"
          style={{
            width: "2.5rem",
            height: "2.5rem",
            background: "var(--brand-purple-600)",
            color: "var(--brand-white)",
          }}
        >
          <SparkleIcon width={18} height={18} />
        </div>
        <span
          className="text-base font-bold"
          style={{ color: "var(--brand-purple-600)" }}
        >
          Estimate Summary
        </span>
        <div className="ml-auto">
          <StatusBadge
            label={orderStatusMapping[order.status]}
            tone={STATUS_BADGE_TONES[order.status] ?? "neutral"}
            icon={STATUS_BADGE_ICONS[order.status]}
          />
        </div>
      </div>
      <div
        className="mt-3 p-3 border-1 border-round-2xl"
        style={{
          background:
            "color-mix(in srgb, var(--brand-purple-600) 6%, var(--brand-white))",
          borderColor:
            "color-mix(in srgb, var(--brand-purple-600) 20%, transparent)",
        }}
      >
        <span
          className="block text-sm"
          style={{ color: "var(--brand-gray-600)" }}
        >
          Amount owed
        </span>
        <span
          className="block text-3xl font-bold mt-1"
          style={{ color: "var(--brand-black)" }}
          data-testid="estimate-summary-amount-owed"
        >
          {parsePrice(centsToDollars(clearPrice || 0))}
        </span>
        <div
          className="mt-3 border-round-3xl overflow-hidden"
          style={{ height: "0.375rem", background: "var(--brand-gray-200)" }}
          aria-hidden
        >
          <div
            className="h-full border-round-3xl"
            style={{
              width: `${collectedPercent}%`,
              background: "var(--brand-purple-600)",
            }}
          />
        </div>
        <div className="flex align-items-center justify-content-between mt-3">
          <span className="text-sm" style={{ color: "var(--brand-gray-600)" }}>
            Collected
          </span>
          <span
            className="text-base font-bold"
            style={{ color: "var(--brand-black)" }}
            data-testid="estimate-summary-collected"
          >
            {parsePrice(centsToDollars(collectedInCents))}
          </span>
        </div>
      </div>
      <div
        className="mt-3 border-top-1"
        style={{
          borderColor: "var(--brand-gray-200)",
          marginLeft: "-1rem",
          marginRight: "-1rem",
        }}
      />
      <div className="flex flex-column gap-3 mt-3">
        {rows.map(({ label, value, prefix }) => (
          <div
            className="flex align-items-center justify-content-between gap-3"
            key={label}
          >
            <span
              className="text-sm"
              style={{ color: "var(--brand-gray-600)" }}
            >
              {label}
            </span>
            <span className="text-sm" style={{ color: "var(--brand-black)" }}>
              {Math.round(value as number) !== 0 && prefix}
              {parsePrice(centsToDollars(value as number))}
            </span>
          </div>
        ))}
      </div>
      <div
        className="flex align-items-center justify-content-between gap-3 mt-3 pt-3 border-top-1"
        style={{ borderColor: "var(--brand-gray-200)" }}
      >
        <span
          className="text-sm font-semibold"
          style={{ color: "var(--brand-black)" }}
        >
          Patient Responsibility
        </span>
        <span
          className="text-sm font-semibold"
          style={{ color: "var(--brand-purple-600)" }}
          data-testid="estimate-summary-patient-responsibility"
        >
          {parsePrice(centsToDollars(clearPrice || 0))}
        </span>
      </div>
    </StyledSummary>
  );
}

export default EstimateSummary;
